import { useCallback, useEffect, useMemo, useState } from 'react'
import { Tooltip } from 'antd'
import { motion } from 'framer-motion'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'

import { SubImageOverlay } from '../type'

// --- Interfaces ---
interface SubImage {
  id: number
  name?: string
  thumbnail?: string
  multires?: string | null | boolean
  tilesUrl?: string | null
}

interface SubImageCarouselType {
  containerRef: React.RefObject<HTMLDivElement>
  isDragging: boolean
  onMouseDown: React.MouseEventHandler
  onMouseMove: React.MouseEventHandler
  onMouseUp: React.MouseEventHandler
  onMouseLeave: React.MouseEventHandler
  onTouchStart: React.TouchEventHandler
  onTouchMove: React.TouchEventHandler
  onTouchEnd: React.TouchEventHandler
  onMouseEnter: React.MouseEventHandler
}

interface ThumbnailItemProps {
  image: SubImage
  matches: boolean
  isSelected: boolean
  isLoading: boolean
  onSelect: (id: number) => void
}

// --- Constants ---
const BREAKPOINTS = { MOBILE: 500, TABLET: 768, LAPTOP: 1024 }
const Z_INDEX = 99999

const getCarouselEvents = (carousel: SubImageCarouselType) => ({
  onMouseDown: carousel.onMouseDown,
  onMouseMove: carousel.onMouseMove,
  onMouseUp: carousel.onMouseUp,
  onMouseLeave: carousel.onMouseLeave,
  onTouchStart: carousel.onTouchStart,
  onTouchMove: carousel.onTouchMove,
  onTouchEnd: carousel.onTouchEnd,
  onMouseEnter: carousel.onMouseEnter
})

// --- Sub-component ---
function ThumbnailItem({
  image,
  matches,
  isSelected,
  isLoading,
  onSelect
}: ThumbnailItemProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const width = matches ? 168 : 90
  const height = matches ? 117 : 65

  let opacityClass = 'opacity-0'
  if (isLoaded) {
    opacityClass = isLoading && !isSelected ? 'opacity-50' : 'opacity-100'
  }

  return (
    <div
      className={`relative group flex-shrink-0 transition-all duration-300 flex flex-col justify-center items-center ${opacityClass} ${
        isLoading && !isSelected ? 'cursor-not-allowed' : 'cursor-pointer'
      }`}
      style={{
        zIndex: isSelected ? 1 : 0,
        width: `${width}px`
      }}
      onClick={() => !isLoading && onSelect(image.id)}
    >
      <motion.div
        initial={{ scale: 1.0 }}
        animate={{ scale: isSelected ? 1.08 : 1.0 }}
        transition={{ duration: 0.1 }}
        style={{
          zIndex: isSelected ? 1 : 0,
          width: matches ? '168px' : '90px',
          height: matches ? '117px' : '65px',
          flexShrink: 0,
          opacity: 1,
          willChange: 'transform, opacity'
        }}
        className={`relative overflow-hidden transition-all duration-150 ${
          isSelected ? 'selected-thumbnail' : 'border-0'
        }`}
      >
        <LazyThumbnail
          imageType={ImageType.OBJECT_FILL}
          url={
            image.multires && image.tilesUrl
              ? `${image.tilesUrl}thumb.jpg`
              : image.thumbnail || ''
          }
          className="w-full h-full object-cover"
          name={image.name || ''}
          width={width}
          height={height}
          onLoad={() => setIsLoaded(true)}
        />
        <Tooltip title={image.name} placement="top" color="#0E0D0A80">
          <div
            className={`absolute inset-0 select-none font-bold text-center flex flex-col justify-end pointer-events-none text-white ${
              matches ? 'px-2 p-2 text-[13px]' : 'px-1 p-1 text-[10px]'
            }`}
          >
            <span
              className="block pointer-events-auto truncate"
              style={{
                maxHeight: matches ? '6.5em' : '5.2em',
                overflow: 'hidden',
                userSelect: 'none'
              }}
            >
              {image.name}
            </span>
          </div>
        </Tooltip>
      </motion.div>
    </div>
  )
}

// --- Main Component ---
export default function SubImageThubnailCarousel({
  subImageOverlay,
  handleClickSubImage,
  subImageCarousel,
  matches,
  parentContainerRef
}: {
  subImageOverlay: SubImageOverlay
  handleClickSubImage: (subImageId: number) => void
  subImageCarousel: SubImageCarouselType
  matches: boolean
  parentContainerRef?: React.RefObject<HTMLDivElement>
}) {
  const points = useRecoilValue(listPointState)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [position, setPosition] = useState({ left: 0, width: 0 })
  const [isParentVisible, setIsParentVisible] = useState(true)

  const { point, subImages } = useMemo(() => {
    const targetPoint = points.find((p) => p.id === subImageOverlay.pointId)
    if (!targetPoint) return { point: null, subImages: [] }

    let images = targetPoint.subImages || []
    if (
      images.length === 0 &&
      currentViewer.pointDetail?.id === targetPoint.id
    ) {
      images = currentViewer.pointDetail.subImages || []
    }

    return {
      point: targetPoint,
      subImages: [...images].sort((a, b) => a.id - b.id)
    }
  }, [points, subImageOverlay.pointId, currentViewer.pointDetail])

  const calculatePosition = useCallback(() => {
    if (!point || !subImageOverlay.isOpen) return

    // Find the real parent element in the DOM to get current position
    const parentEl = document.querySelector(
      `[data-point-id="${subImageOverlay.pointId}"]`
    ) as HTMLElement | null

    if (!parentEl) return

    const parentRect = parentEl.getBoundingClientRect()
    const containerEl = parentContainerRef?.current

    // Check visibility if parentContainer is provided
    if (containerEl) {
      const containerRect = containerEl.getBoundingClientRect()
      const isVisible =
        parentRect.right >= containerRect.left &&
        parentRect.left <= containerRect.right

      setIsParentVisible(isVisible)
    }

    const screenWidth = window.innerWidth
    let maxWidthLimit = 984

    if (screenWidth < BREAKPOINTS.MOBILE) maxWidthLimit = 320
    else if (screenWidth < BREAKPOINTS.TABLET) maxWidthLimit = 408
    else if (screenWidth < BREAKPOINTS.LAPTOP) maxWidthLimit = 792

    const imgWidth = matches ? 168 : 90
    const gap = matches ? 24 : 8 // space-x-6 (24px) and space-x-2 (8px)
    const paddingX = matches ? 48 : 16 // px-6 (total 48px) and px-2 (total 16px)

    const totalContentWidth =
      subImages.length * imgWidth +
      Math.max(0, subImages.length - 1) * gap +
      paddingX

    const finalWidth = Math.min(
      maxWidthLimit,
      screenWidth - 20,
      totalContentWidth
    )

    let finalLeft = parentRect.left // Use real DOM position instead of static prop

    if (screenWidth < BREAKPOINTS.LAPTOP) {
      // Mobile & Tablet
      const minLeft = 10
      const maxLeft = screenWidth - finalWidth - 10

      finalLeft = parentRect.left + parentRect.width / 2 - finalWidth / 2
      finalLeft = Math.max(minLeft, Math.min(finalLeft, maxLeft))
    } else {
      // Desktop logic
      const rightRef = 180
      const leftRef = 45
      const maxLeft = screenWidth - finalWidth - 10

      if (finalLeft < leftRef) {
        finalLeft = parentRect.left + parentRect.width / 4 - leftRef
      } else if (finalLeft > maxLeft) {
        finalLeft = parentRect.left - finalWidth + rightRef
      }
    }

    setPosition({ left: finalLeft, width: finalWidth })
  }, [
    point,
    subImages.length,
    matches,
    subImageOverlay.isOpen,
    subImageOverlay.pointId,
    parentContainerRef
  ])

  useEffect(() => {
    calculatePosition()

    const handleScroll = () => {
      // Trigger re-calculation on scroll
      calculatePosition()
    }

    const container = parentContainerRef?.current
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true })
    }

    window.addEventListener('resize', calculatePosition)
    return () => {
      window.removeEventListener('resize', calculatePosition)
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [calculatePosition, parentContainerRef])

  if (!subImageOverlay.isOpen || !point || !isParentVisible) return null

  const imgWidth = matches ? 168 : 90
  const gap = matches ? 24 : 8
  const paddingX = matches ? 48 : 16
  const isOverflowing =
    subImages.length * imgWidth + (subImages.length - 1) * gap + paddingX >
    position.width + 1 // Add 1px buffer to avoid rounding issues

  return (
    <div
      id="subimage-overlay"
      style={{
        position: 'fixed',
        bottom: matches ? '165px' : '130px',
        left: position.left,
        width: position.width,
        zIndex: Z_INDEX,
        boxSizing: 'border-box'
      }}
      className={`bg-white/50 backdrop-blur-[4px] border border-gray-200 transition-all duration-300 ease-out animate-subimage-fadein
        ${
          subImageOverlay.isOpen
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-95'
        }`}
    >
      <div
        ref={subImageCarousel.containerRef}
        {...getCarouselEvents(subImageCarousel)}
        className={`flex ${
          matches ? 'space-x-6 h-[162px] px-6' : 'space-x-2 h-[90px] px-2'
        } items-center overflow-x-auto no-scrollbar
          ${isOverflowing ? 'justify-start' : 'justify-center'}`}
        style={{
          cursor: subImageCarousel.isDragging ? 'grabbing' : 'grab',
          scrollbarWidth: 'none',
          userSelect: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {subImages?.length > 0 &&
          subImages.map((img) => (
            <ThumbnailItem
              key={img.id}
              image={img}
              matches={matches}
              isSelected={img.id === currentViewer.selectedImage}
              isLoading={currentViewer.imageIsLoading ?? false}
              onSelect={(id: number) => {
                const newSelected =
                  id !== currentViewer.selectedImage
                    ? id
                    : currentViewer.pointDetail?.image360?.id
                setCurrentViewer({
                  ...currentViewer,
                  selectedImage: newSelected
                })
                if (id !== currentViewer.selectedImage) handleClickSubImage(id)
              }}
            />
          ))}
      </div>
    </div>
  )
}

SubImageThubnailCarousel.defaultProps = {
  parentContainerRef: { current: null }
}
